/**
 * CORNER_V2: Signing-aware types for the PdfSigningViewer.
 * FieldDescriptor normalizes SignatureField into a viewer-consumable shape
 * with explicit signing state, party assignment, bounding box, and candidate metadata.
 *
 * Coordinates are PERCENT-BASED (0-100) — no conversion needed from backend.
 */
import type { SignatureField, SigningSlot } from '@corner/shared';
import { asFiniteNumber } from '../../lib/coerceNumeric';

export type ViewerMode = 'sign' | 'configure';

export interface FieldDescriptor {
  id: string;
  type: 'signature' | 'initials' | 'date' | 'text';
  page: number;
  rect: { x: number; y: number; width: number; height: number }; // percent-based 0-100
  signed: boolean;
  isForCurrentSigner: boolean;
  partyId: string;
  label: string;
  /** Detection confidence 0-1 (from SignatureTargetCandidate pipeline). Higher = more reliable. */
  confidence?: number;
  /** Party role hint from detection (e.g., 'buyer', 'agent'). */
  partyHint?: string;
  /** Semantic role (e.g., 'buyer_signature', 'agent_date'). */
  role?: string;
  /** How the placeholder is delimited visually. Drives live-overlay rendering:
   *  bracketed/highlighted placeholders need an opaque cover when filled. */
  placeholderStyle?: 'square' | 'curly' | 'angle' | 'paren' | 'highlight' | 'underline' | 'anchor';
  /** Raw placeholder content as it appears in the PDF ("[BUYER NAME]"). */
  placeholderRaw?: string;
  /** How this descriptor was created. */
  source: {
    kind: 'slot' | 'field';
    slotId?: string;
    fieldId?: string;
    /** Original SignatureField for legacy callbacks */
    signatureField?: SignatureField;
  };
  /** Index in the original source array */
  sourceIndex: number;
  /** v1 placement-redesign: visual state. Defaults to 'suggested' when missing. */
  state?: 'suggested' | 'confirmed' | 'signed';
  /** v1 placement-redesign: which signer this marker belongs to (index into
   *  the signers array). -1 (or undefined) means unassigned — falls back to
   *  the first palette slot. */
  signerIndex?: number;
  /** Recipient/sender ownership independent of the currently selected legend row. */
  isRecipientParty?: boolean;
}

/** Lightweight signer shape used by buildFieldDescriptors to assign
 *  signerIndex via role-key matching. Matches the createSigningRequest
 *  signer shape (only the role/email fields are needed here). */
export interface ViewerSigner {
  email: string;
  name: string;
  role: string;
}

export interface PageFieldInfo {
  pageNumber: number;
  fieldsForCurrentSigner: FieldDescriptor[];
  fieldsForOtherParties: FieldDescriptor[];
  allFields: FieldDescriptor[];
}

/**
 * Build FieldDescriptor[] from SignatureField[] + current signer context.
 * This is the SINGLE NORMALIZATION POINT — all viewer components consume FieldDescriptor.
 *
 * Multi-party logic:
 * - If `currentSignerId` is provided, matches against `signer`, `party`, or `signedByEmail`.
 * - Uses `assignedSignerIndex` when available (multi-party invite flow).
 * - Falls back to `signer === 'you' || party === 'self'` for self-sign flow.
 */
export function buildFieldDescriptors(
  fields: SignatureField[],
  currentSignerId?: string,
  signers?: ViewerSigner[],
  /** When set (e.g. pdf.js page count), clamp field page so overlays still render. */
  maxPage?: number | null,
): FieldDescriptor[] {
  // Normalize signer role keys once for cheap lookup. Role on a field is
  // typically `<role>_signature` / `_date` / `_initials` / `_name`, so we
  // strip the suffix and match on the bare role key.
  const normalizedSigners = (signers ?? []).map((s) => ({
    ...s,
    roleKey: s.role.toLowerCase().replace(/[\s_-]/g, ''),
  }));

  return fields.map((f, i) => {
    let page = Math.max(1, Math.floor(asFiniteNumber(f.page) ?? 1));
    if (maxPage != null && maxPage >= 1 && page > maxPage) page = maxPage;
    const x = asFiniteNumber(f.x) ?? 0;
    const y = asFiniteNumber(f.y) ?? 0;
    const width = asFiniteNumber(f.width) ?? 0;
    const height = asFiniteNumber(f.height) ?? 0;

    let isForCurrentSigner: boolean;
    if (currentSignerId) {
      isForCurrentSigner =
        f.signer === 'you' ||
        f.party === 'self' ||
        f.signedByEmail === currentSignerId;
    } else {
      isForCurrentSigner = f.signer !== 'other' && f.party !== 'other';
    }

    const fieldRoleBare = f.role?.replace(/_(signature|date|initials|name|title|email)$/, '') ?? '';
    const partyId = fieldRoleBare || (isForCurrentSigner ? 'self' : 'other');

    // Resolve signerIndex by matching bare role-key, then by signedByEmail
    // as a fallback. -1 = unassigned.
    let signerIndex = -1;
    if (normalizedSigners.length > 0) {
      const idx = asFiniteNumber(f.assignedSignerIndex);
      if (idx != null && idx >= 0) signerIndex = Math.floor(idx);
      const fieldRoleKey = fieldRoleBare.toLowerCase().replace(/[\s_-]/g, '');
      const byRole = normalizedSigners.findIndex((s) => s.roleKey === fieldRoleKey);
      if (signerIndex < 0 && byRole >= 0) signerIndex = byRole;
      else if (signerIndex < 0 && f.signedByEmail) {
        const byEmail = normalizedSigners.findIndex((s) => s.email === f.signedByEmail);
        if (byEmail >= 0) signerIndex = byEmail;
      }
    }
    let assignedSigner = signerIndex >= 0 ? normalizedSigners[signerIndex] : null;
    const isRecipientParty =
      f.party === 'other' ||
      f.signer === 'other' ||
      assignedSigner?.role === 'other';

    // Unassigned fields must not default to palette slot 0 (owner green): map by party
    // so recipient markers stay blue (slot 1) even when AI omits assignedSignerIndex.
    if (signerIndex < 0 && normalizedSigners.length > 0) {
      const selfIdx = normalizedSigners.findIndex((s) => s.role === 'self');
      const otherIdx = normalizedSigners.findIndex((s) => s.role === 'other');
      if (isRecipientParty) {
        signerIndex = otherIdx >= 0 ? otherIdx : 1;
      } else {
        signerIndex = selfIdx >= 0 ? selfIdx : 0;
      }
      assignedSigner = signerIndex >= 0 ? normalizedSigners[signerIndex] : null;
    }

    // Resolve visual state. Backend may set `state` explicitly; otherwise
    // signed fields are 'signed' and everything else defaults to 'suggested'.
    const state: 'suggested' | 'confirmed' | 'signed' =
      f.state ?? (f.placed || f.signedAt ? 'signed' : 'suggested');

    return {
      id: f.id ?? `field-${page}-${i}`,
      type: f.type ?? 'signature',
      page,
      rect: { x, y, width, height },
      signed: !!(f.placed || f.signedAt),
      isForCurrentSigner,
      partyId,
      label: f.label,
      confidence: f.confidence,
      partyHint: f.partyHint,
      role: f.role,
      placeholderStyle: f.placeholderStyle,
      placeholderRaw: f.placeholderRaw,
      source: { kind: 'field' as const, fieldId: f.id, signatureField: f },
      sourceIndex: i,
      state,
      signerIndex,
      isRecipientParty,
    };
  });
}

/**
 * Build FieldDescriptor[] from SigningSlot[] (the v2 canonical source).
 * Prefer this over buildFieldDescriptors when slots are available.
 */
export function buildFieldDescriptorsFromSlots(
  slots: SigningSlot[],
  currentSignerId: string,
): FieldDescriptor[] {
  return slots.map((slot, i) => ({
    id: slot.id,
    type: slot.type,
    page: slot.pageNumber,
    rect: { ...slot.rect },
    signed: slot.status === 'signed',
    isForCurrentSigner: slot.partyId === currentSignerId,
    partyId: slot.partyId,
    label: slot.label ?? slot.role?.replace(/_/g, ' ') ?? 'Field',
    confidence: slot.confidence,
    partyHint: slot.partyHint,
    role: slot.role,
    source: {
      kind: 'slot' as const,
      slotId: slot.id,
      fieldId: slot.createdFrom.signatureFieldId,
    },
    sourceIndex: i,
  }));
}

/**
 * Group FieldDescriptors by page number into PageFieldInfo[].
 */
export function buildPageFieldIndex(
  descriptors: FieldDescriptor[],
  numPages: number,
): PageFieldInfo[] {
  const byPage = new Map<number, FieldDescriptor[]>();
  for (const d of descriptors) {
    const arr = byPage.get(d.page) ?? [];
    arr.push(d);
    byPage.set(d.page, arr);
  }

  return Array.from({ length: numPages }, (_, i) => {
    const page = i + 1;
    const all = byPage.get(page) ?? [];
    return {
      pageNumber: page,
      fieldsForCurrentSigner: all.filter((f) => f.isForCurrentSigner),
      fieldsForOtherParties: all.filter((f) => !f.isForCurrentSigner),
      allFields: all,
    };
  });
}

/**
 * Find the next unsigned field for the current signer.
 * Sort order: page → Y position → confidence (higher first for tie-breaking).
 * When confidence data is available from SignatureTargetCandidate, higher-confidence
 * fields are preferred when multiple pending fields exist at similar positions.
 */
export function findNextPendingField(
  descriptors: FieldDescriptor[],
  afterId?: string,
  mode?: ViewerMode,
): FieldDescriptor | null {
  const candidates = mode === 'configure'
    ? descriptors.filter((f) => !f.signed)
    : descriptors.filter((f) => f.isForCurrentSigner && !f.signed);

  // Sort by page, then Y position, then confidence (higher first for same page+Y)
  candidates.sort((a, b) => {
    if (a.page !== b.page) return a.page - b.page;
    if (Math.abs(a.rect.y - b.rect.y) > 2) return a.rect.y - b.rect.y; // 2% tolerance for "same row"
    // Same page, similar Y — prefer higher confidence
    return (b.confidence ?? 0) - (a.confidence ?? 0);
  });

  if (!afterId) return candidates[0] ?? null;

  const afterIdx = candidates.findIndex((f) => f.id === afterId);
  if (afterIdx >= 0 && afterIdx + 1 < candidates.length) return candidates[afterIdx + 1];
  // Wrap around
  return candidates[0] ?? null;
}
